import { useCallback, useContext, useMemo } from 'react';

import {
  useDealPackageConfigsQuery,
  useUpsertDealPackageConfigsMutation,
} from '../gql/generated/graphql';

import { DealContext } from '../libs/DealContext';
import { PersistedPackageConfig } from '../types/products';

export const useDealPackageConfigs = () => {
  const { deal } = useContext(DealContext);
  const dealId = deal?.id ? Number(deal.id) : 0;

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useDealPackageConfigsQuery({
    variables: { dealId },
    skip: !dealId,
    fetchPolicy: 'cache-and-network',
  });

  const [upsertMutation, { loading: mutationLoading, error: mutationError }] =
    useUpsertDealPackageConfigsMutation();

  const configs = useMemo(() => data?.dealPackageConfigs ?? [], [data?.dealPackageConfigs]);

  const saveConfigs = useCallback(
    async (configsToSave: PersistedPackageConfig[]) => {
      if (!dealId) {
        throw new Error('No deal ID available');
      }

      const result = await upsertMutation({
        variables: {
          input: {
            dealId,
            configs: configsToSave.map((config) => ({
              slotIndex: config.slotIndex,
              selectedProductSlugs: config.selectedProductSlugs,
            })),
          },
        },
      });

      return result;
    },
    [dealId, upsertMutation],
  );

  return {
    configs,
    loading: queryLoading || mutationLoading,
    queryLoading,
    error: queryError || mutationError,
    queryError,
    mutationError,
    saveConfigs,
    refetch,
  };
};
